/**
 * useDirectoryManifest — Hook to slice the pre-built navigation manifest
 *
 * Reads from the static directory-navigation-manifest.json (compiled at build time).
 * Each directory pattern component calls: useDirectoryManifest(directorySequenceNum)
 * and gets its directory metadata + buttons — zero network calls.
 *
 * KEY CHANGE (2026-04-02): Manifest is now keyed by Directory_Sequence_Num (DRv2 PK),
 * NOT Component_Sequence_Num. Component_Sequence_Num is a separate field that identifies
 * which renderer template to use (10000=CARD, 10002=SHAFT, 10974=BNDR).
 *
 * Unified model (2026-03-30): All directories share one table & one hook.
 * Created: 2026-03-22
 */

import { useMemo } from 'react';
import type { ManifestButton, ManifestDirectory } from './manifest-types';

// Import the pre-built manifest (compiled by generate-directory-manifest.js)
// This is a static JSON import — Vite resolves it at build time.
import manifest from '../../../directory-navigation-manifest.json';

/**
 * Get a single directory's metadata + buttons from the manifest.
 * @param directorySequenceNum - The Directory_Sequence_Num (DRv2 PK) to look up.
 * Returns null if the directory isn't in the manifest.
 */
export function useDirectoryManifest(directorySequenceNum: number): ManifestDirectory | null {
    return useMemo(() => {
        const key = String(directorySequenceNum);
        const dir = (manifest as any).directories?.[key];
        return dir ?? null;
    }, [directorySequenceNum]);
}

/**
 * Get the app default sequence number from the manifest.
 */
export function useAppDefault(): string | null {
    return (manifest as any).appDefault ?? null;
}

/**
 * Get all persistent overlay sequence numbers from the manifest.
 */
export function usePersistentOverlays(): string[] {
    return (manifest as any).persistentOverlays ?? [];
}

/**
 * Resolve a binder directory entry. Returns the binder metadata + its buttons
 * split into component targets and appendix targets.
 *
 * @param directorySequenceNum - The Directory_Sequence_Num (DRv2 PK) of the binder.
 *
 * A binder (pattern BNDR) hosts:
 *   - Component buttons (prefix contains 'com') → the main component section
 *   - Appendix buttons (prefix contains 'appendix') → the appendix section
 *   - Other buttons (dirs, dirm) → additional navigation
 */
export function useBinderManifest(directorySequenceNum: number): {
    binder: ManifestDirectory;
    componentButtons: ManifestButton[];
    appendixButtons: ManifestButton[];
} | null {
    return useMemo(() => {
        const key = String(directorySequenceNum);
        const dir = (manifest as any).directories?.[key] as ManifestDirectory | undefined;
        if (!dir) return null;

        const buttons: ManifestButton[] = dir.buttons ?? [];
        const componentButtons = buttons.filter(
            (b) => b.prefix?.includes('com') || b.assignmentType === 'com'
        );
        const appendixButtons = buttons.filter(
            (b) => b.prefix?.includes('appendix') || b.assignmentType === 'appendix'
        );

        return { binder: dir, componentButtons, appendixButtons };
    }, [directorySequenceNum]);
}

/**
 * Resolve an appendix directory entry. An appendix (pattern APPX) is a
 * sub-directory that hosts tab assignments rendered as a MUMultiTab container.
 * Returns the appendix metadata + its tab buttons.
 *
 * @param directorySequenceNum - The Directory_Sequence_Num (DRv2 PK) of the appendix.
 */
export function useAppendixManifest(directorySequenceNum: number): {
    appendix: ManifestDirectory;
    tabButtons: ManifestButton[];
} | null {
    return useMemo(() => {
        const key = String(directorySequenceNum);
        const dir = (manifest as any).directories?.[key] as ManifestDirectory | undefined;
        if (!dir) return null;

        const tabButtons: ManifestButton[] = (dir.buttons ?? []).filter(
            (b) => b.prefix?.includes('tab') || b.assignmentType === 'tab'
        );

        return { appendix: dir, tabButtons };
    }, [directorySequenceNum]);
}

/**
 * Resolve a directory by Component_Sequence_Num (CR2 FK).
 * Used by master templates that extract the CR2 seq from the URL.
 * Returns the FIRST matching directory (use appDefault as tiebreaker).
 * If multiple directories share the same Component_Sequence_Num,
 * prefers the one marked as Is_App_Default.
 *
 * @param componentSequenceNum - The Component_Sequence_Num (CR2 PK) from the URL.
 */
export function useDirectoryByComponent(componentSequenceNum: number): ManifestDirectory | null {
    return useMemo(() => {
        const compKey = String(componentSequenceNum);
        const dirKeys: string[] = (manifest as any).componentIndex?.[compKey] ?? [];
        if (dirKeys.length === 0) return null;

        // If only one directory uses this component, return it directly
        if (dirKeys.length === 1) {
            return (manifest as any).directories?.[dirKeys[0]] ?? null;
        }

        // Multiple directories share this component — prefer the app default
        const dirs = dirKeys
            .map((k: string) => (manifest as any).directories?.[k] as ManifestDirectory | undefined)
            .filter(Boolean) as ManifestDirectory[];

        return dirs.find(d => d.isAppDefault) ?? dirs[0] ?? null;
    }, [componentSequenceNum]);
}

export default manifest;
